<script setup>
import { computed } from "vue";
import { siteCopy } from "../data/content";
import ContactForm from "../components/ContactForm.vue";
import IconArrow from "../components/IconArrow.vue";
import ProjectsCarousel from "../components/ProjectsCarousel.vue";
import SiteFooter from "../components/SiteFooter.vue";
import SiteHeader from "../components/SiteHeader.vue";

const props = defineProps({
  locale: { type: String, required: true },
});

const copy = computed(() => siteCopy[props.locale]);
const organisationSchema = computed(() => JSON.stringify({
  "@context": "https://schema.org",
  "@type": "ProfessionalService",
  name: "Resi Corporation",
  url: `https://corpresi.com/${props.locale}`,
  email: "resicorporation@gmail.com",
  address: { "@type": "PostalAddress", addressLocality: "Buenos Aires", addressCountry: "AR" },
  areaServed: ["Argentina", "United Kingdom", "Italy"],
  sameAs: [
    "https://www.instagram.com/resicorporation/",
    "https://www.tiktok.com/@resi.corporation",
    "https://www.facebook.com/profile.php?id=61575279148019&locale=es_LA",
  ],
}));
</script>

<template>
  <div class="site-page" :lang="locale">
    <SiteHeader :locale="locale" :nav="copy.nav" />

    <main id="main">
      <section class="hero">
        <div class="hero__art" aria-hidden="true">
          <div class="hero__sun" />
          <span class="hero__constellation hero__constellation--one">✦</span>
          <span class="hero__constellation hero__constellation--two">✧</span>
          <span class="hero__orbit hero__orbit--one" />
          <span class="hero__orbit hero__orbit--two" />
        </div>
        <div class="shell hero__grid">
          <div class="hero__content">
            <p class="eyebrow hero__eyebrow">{{ copy.hero.eyebrow }}</p>
            <h1>{{ copy.hero.title }}</h1>
            <p class="hero__description">{{ copy.hero.description }}</p>
            <div class="hero__actions">
              <a class="button button--primary" href="#contact">{{ copy.hero.primary }}<IconArrow /></a>
              <a class="button button--ghost" href="#projects">{{ copy.hero.secondary }}</a>
            </div>
            <div class="hero__stats" aria-label="Resi Corporation">
              <span v-for="stat in copy.hero.stats" :key="stat">{{ stat }}</span>
            </div>
          </div>
          <div class="hero__stage" aria-label="Creative development process">
            <div class="code-plaque">
              <span class="code-plaque__line" />
              <code>{{ copy.hero.code }}</code>
            </div>
            <div class="orbit-labels" aria-hidden="true">
              <span
                v-for="(label, index) in copy.hero.orbit"
                :key="label"
                :style="{ '--orbit-index': index }"
              >
                {{ label }}
              </span>
            </div>
          </div>
        </div>
        <a class="scroll-cue" href="#services" :aria-label="copy.nav.services"><span />Scroll</a>
      </section>

      <section id="services" class="section intro-section">
        <div class="shell">
          <div class="section-heading section-heading--wide" data-reveal>
            <p class="eyebrow">{{ copy.intro.kicker }}</p>
            <h2>{{ copy.intro.title }}</h2>
            <p>{{ copy.intro.body }}</p>
          </div>
          <div class="service-grid">
            <article v-for="service in copy.services.items" :key="service.index" class="service-card" data-reveal>
              <span class="service-card__index">{{ service.index }}</span>
              <h3>{{ service.title }}</h3>
              <p>{{ service.description }}</p>
              <small>{{ service.detail }}</small>
              <div class="service-card__glyph" aria-hidden="true"><span /><span /></div>
            </article>
          </div>
        </div>
      </section>

      <section class="section arsenal-section">
        <div class="shell arsenal-grid">
          <div class="section-heading" data-reveal>
            <p class="eyebrow">{{ copy.arsenal.kicker }}</p>
            <h2>{{ copy.arsenal.title }}</h2>
            <p>{{ copy.arsenal.body }}</p>
          </div>
          <div class="arsenal-list" data-reveal>
            <div v-for="group in copy.arsenal.groups" :key="group.title">
              <span>{{ group.title }}</span>
              <p>{{ group.values.join(" · ") }}</p>
            </div>
          </div>
        </div>
      </section>

      <section id="process" class="section process-section">
        <div class="process-backdrop" aria-hidden="true" />
        <div class="shell">
          <div class="section-heading process-heading" data-reveal>
            <p class="eyebrow">{{ copy.process.kicker }}</p>
            <h2>{{ copy.process.title }}</h2>
            <p>{{ copy.process.description }}</p>
          </div>
          <div class="process-grid">
            <article v-for="step in copy.process.steps" :key="step.number" class="process-step" data-reveal>
              <span>{{ step.number }}</span>
              <h3>{{ step.title }}</h3>
              <p>{{ step.body }}</p>
            </article>
          </div>
        </div>
      </section>

      <section id="projects" class="section projects-section">
        <div class="shell">
          <div class="section-heading section-heading--split" data-reveal>
            <div>
              <p class="eyebrow">{{ copy.projects.kicker }}</p>
              <h2>{{ copy.projects.title }}</h2>
            </div>
            <p>{{ copy.projects.description }}</p>
          </div>
          <ProjectsCarousel :locale="locale" :copy="copy.projects" />
        </div>
      </section>

      <section class="section testimonials-section">
        <div class="shell">
          <div class="section-heading" data-reveal>
            <p class="eyebrow">{{ copy.testimonials.kicker }}</p>
            <h2>{{ copy.testimonials.title }}</h2>
          </div>
          <div class="testimonials-track">
            <blockquote v-for="(quote, index) in copy.testimonials.items" :key="quote">
              <span>“</span>
              <p>{{ quote }}</p>
              <footer>0{{ index + 1 }} · {{ copy.testimonials.role }}</footer>
            </blockquote>
          </div>
        </div>
      </section>

      <section class="section journal-teaser">
        <div class="shell section-heading section-heading--split" data-reveal>
          <div>
            <p class="eyebrow">{{ copy.blog.kicker }}</p>
            <h2>{{ copy.blog.title }}</h2>
          </div>
          <div>
            <p>{{ copy.blog.description }}</p>
            <RouterLink class="inline-link" :to="`/${locale}/blog`">{{ copy.blog.read }}<IconArrow /></RouterLink>
          </div>
        </div>
      </section>

      <section id="contact" class="section contact-section">
        <div class="shell contact-grid">
          <div class="section-heading contact-heading" data-reveal>
            <p class="eyebrow">{{ copy.contact.kicker }}</p>
            <h2>{{ copy.contact.title }}</h2>
            <p>{{ copy.contact.description }}</p>
            <div class="direct-contact">
              <span>{{ copy.contact.direct }}</span>
              <a href="mailto:resicorporation@gmail.com" data-analytics="contact_email">resicorporation@gmail.com</a>
              <a
                href="https://api.whatsapp.com/send?phone=541168803040"
                target="_blank"
                rel="noreferrer"
                data-analytics="contact_whatsapp"
                data-analytics-label="Máximo"
              >
                WhatsApp · Máximo
              </a>
            </div>
            <div class="contact-socials" :aria-label="copy.contact.social">
              <a
                class="contact-social contact-social--instagram"
                href="https://www.instagram.com/resicorporation/"
                target="_blank"
                rel="noreferrer"
                data-analytics="contact_instagram"
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <rect x="3" y="3" width="18" height="18" rx="5" stroke="currentColor" stroke-width="2" />
                  <circle cx="12" cy="12" r="4.15" stroke="currentColor" stroke-width="2" />
                  <circle cx="17.4" cy="6.7" r="1.15" fill="currentColor" />
                </svg>
                <span>Instagram</span>
              </a>
              <a
                class="contact-social contact-social--tiktok"
                href="https://www.tiktok.com/@resi.corporation"
                target="_blank"
                rel="noreferrer"
                data-analytics="contact_tiktok"
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                  <path d="M14.2 3h3.05a5.1 5.1 0 0 0 3.7 3.72v3.08a8.1 8.1 0 0 1-3.7-1.08v6.12a6.12 6.12 0 1 1-5.28-6.07v3.14a3.08 3.08 0 1 0 2.23 2.93V3Z" />
                </svg>
                <span>TikTok</span>
              </a>
              <a
                class="contact-social contact-social--facebook"
                href="https://www.facebook.com/profile.php?id=61575279148019&locale=es_LA"
                target="_blank"
                rel="noreferrer"
                data-analytics="contact_facebook"
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                  <path d="M13.7 21v-8h2.68l.4-3.12H13.7V7.89c0-.9.25-1.52 1.55-1.52h1.66V3.58a22.2 22.2 0 0 0-2.42-.13c-2.4 0-4.04 1.47-4.04 4.16v2.27H7.74V13h2.71v8h3.25Z" />
                </svg>
                <span>Facebook</span>
              </a>
            </div>
          </div>
          <div data-reveal>
            <ContactForm :locale="locale" :copy="copy.contact" />
          </div>
        </div>
      </section>
    </main>

    <SiteFooter :locale="locale" :copy="copy.footer" />
    <component :is="'script'" type="application/ld+json" v-html="organisationSchema" />
  </div>
</template>
